import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WellnessAdminBanner } from '../components/WellnessAdminBanner';
import { adminApi } from '../services/api';

interface MonthStats {
  newUsers: number;
  newUsersChange: number | null;
  bookings: number;
  bookingsChange: number | null;
  revenue: number;
  revenueChange: number | null;
}

interface DashboardStats {
  totalUsers: number;
  totalTherapists: number;
  totalBookings: number;
  totalRevenue: number;
  pendingTherapists: number;
  platformCommission: number;
  completedBookings: number;
  cancelledBookings: number;
  completionRate: number;
  month: MonthStats;
}

interface TimePoint {
  date: string;
  value: number;
}

interface TimeSeries {
  bookings: TimePoint[];
  revenue: TimePoint[];
  users: TimePoint[];
}

type TsMetric = 'bookings' | 'revenue' | 'users';

function isDashboardStats(data: unknown): data is DashboardStats {
  if (!data || typeof data !== 'object') return false;
  const month = (data as DashboardStats).month;
  return (
    typeof month === 'object' &&
    month !== null &&
    typeof month.newUsers === 'number' &&
    'newUsersChange' in month
  );
}

function delta(change: number | null) {
  if (change === null) return null;
  const positive = change >= 0;
  return (
    <span className={`admin-delta ${positive ? 'admin-delta-up' : 'admin-delta-down'}`}>
      {positive ? '▲' : '▼'} {Math.abs(change)}% vs last 30 days
    </span>
  );
}

function DashboardSkeleton() {
  return (
    <div className="admin-dashboard admin-dashboard--fit">
      <div className="admin-skeleton-banner" />
      <div className="admin-stat-grid grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="admin-skeleton-card" style={{ animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
    </div>
  );
}

function MiniChart({
  points,
  color,
  metric,
}: {
  points: TimePoint[];
  color: string;
  metric: TsMetric;
}) {
  if (points.length < 2) {
    return <div className="admin-chart-empty">No trend data for this period yet.</div>;
  }

  const max = Math.max(...points.map((p) => p.value), 1);
  const w = 320;
  const h = 64;
  const pad = 4;
  const xs = points.map((_, i) => pad + (i / (points.length - 1)) * (w - pad * 2));
  const ys = points.map((p) => pad + (h - pad * 2) - (p.value / max) * (h - pad * 2));
  const line = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i]!.toFixed(1)}`)
    .join(' ');
  const area = `${line} L${xs[xs.length - 1]!.toFixed(1)},${h - pad} L${xs[0]!.toFixed(1)},${h - pad} Z`;
  const gradId = `admin-chart-grad-${metric}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="admin-trend-chart" aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} className="admin-chart-area" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        className="admin-chart-line"
      />
      {xs.map((x, i) => (
        <circle
          key={points[i]!.date}
          cx={x}
          cy={ys[i]}
          r={i === xs.length - 1 ? 4 : 2.5}
          fill={color}
          className={i === xs.length - 1 ? 'admin-chart-dot-latest' : 'admin-chart-dot'}
        />
      ))}
    </svg>
  );
}

const STAT_ICONS: Record<string, string> = {
  'Total Users': '◉',
  'Pending Verifications': '⏳',
  'Total Bookings': '◎',
  'Completion Rate': '✦',
  'Total Revenue (₦)': '₦',
  'Platform Commission (₦)': '◈',
};

const STAT_ACCENTS: Record<string, string> = {
  'Total Users': 'admin-stat-accent-sage',
  'Pending Verifications': 'admin-stat-accent-gold',
  'Total Bookings': 'admin-stat-accent-teal',
  'Completion Rate': 'admin-stat-accent-mint',
  'Total Revenue (₦)': 'admin-stat-accent-green',
  'Platform Commission (₦)': 'admin-stat-accent-forest',
};

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [ts, setTs] = useState<TimeSeries | null>(null);
  const [tsMetric, setTsMetric] = useState<TsMetric>('bookings');
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi<DashboardStats>('/admin/dashboard')
      .then((data) => {
        if (!isDashboardStats(data)) {
          throw new Error('Unexpected dashboard response');
        }
        setStats(data);
      })
      .catch((err) => setError((err as Error).message || 'Failed to load dashboard'));

    adminApi<TimeSeries>('/admin/dashboard/timeseries?days=30')
      .then(setTs)
      .catch(() => {});
  }, []);

  if (error) {
    return (
      <div className="py-16 text-center text-red-600">
        <p>{error}</p>
        <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  if (!stats?.month) {
    return <DashboardSkeleton />;
  }

  const topCards = [
    {
      label: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      sub: delta(stats.month.newUsersChange),
      highlight: false,
      href: '/users',
    },
    {
      label: 'Pending Verifications',
      value: stats.pendingTherapists,
      sub:
        stats.pendingTherapists > 0 ? (
          <Link to="/therapists/pending" className="admin-stat-link">
            Review now →
          </Link>
        ) : null,
      highlight: stats.pendingTherapists > 0,
      href: '/therapists/pending',
    },
    {
      label: 'Total Bookings',
      value: stats.totalBookings.toLocaleString(),
      sub: delta(stats.month.bookingsChange),
      highlight: false,
      href: '/sessions',
    },
    {
      label: 'Completion Rate',
      value: `${stats.completionRate}%`,
      sub: (
        <span className="text-xs text-gray-500">
          {stats.completedBookings} completed · {stats.cancelledBookings} cancelled
        </span>
      ),
      highlight: false,
      href: '/sessions',
    },
    {
      label: 'Total Revenue (₦)',
      value: `₦${stats.totalRevenue.toLocaleString()}`,
      sub: delta(stats.month.revenueChange),
      highlight: false,
      href: '/payouts',
    },
    {
      label: 'Platform Commission (₦)',
      value: `₦${stats.platformCommission.toLocaleString()}`,
      sub: null,
      highlight: false,
      href: '/payouts',
    },
  ];

  const monthCards = [
    { label: 'New Users', period: 'Last 30 days', value: stats.month.newUsers.toLocaleString() },
    { label: 'Bookings', period: 'Last 30 days', value: stats.month.bookings.toLocaleString() },
    {
      label: 'Revenue',
      period: 'Last 30 days',
      value: `₦${stats.month.revenue.toLocaleString()}`,
    },
  ];

  const tsPoints = ts?.[tsMetric] ?? [];
  const tsColors: Record<TsMetric, string> = {
    bookings: '#2A697B',
    revenue: '#215667',
    users: '#A16207',
  };

  const latestPoint = tsPoints[tsPoints.length - 1];

  return (
    <div className="admin-dashboard admin-dashboard--fit">
      <WellnessAdminBanner pendingVerifications={stats.pendingTherapists} compact />

      <div className="admin-section-header">
        <div>
          <h2 className="admin-section-title">Platform overview</h2>
        </div>
        <span className="admin-updated-pill">Updated just now</span>
      </div>

      <div className="admin-stat-grid grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {topCards.map((card, index) => (
          <Link
            key={card.label}
            to={card.href}
            className={`admin-stat-card ${STAT_ACCENTS[card.label] ?? ''} ${card.highlight ? 'admin-stat-card-alert' : ''}`}
            style={{ animationDelay: `${index * 60}ms` }}
          >
            <div className="admin-stat-card-top">
              <span className="admin-stat-icon" aria-hidden>
                {STAT_ICONS[card.label]}
              </span>
              <p className="admin-stat-label">{card.label}</p>
            </div>
            <p className={`admin-stat-value ${card.highlight ? 'text-amber-800' : ''}`}>
              {card.value}
            </p>
            {card.sub ? <div className="admin-stat-meta">{card.sub}</div> : null}
          </Link>
        ))}
      </div>

      <div className="admin-dashboard-lower">
        <div className="admin-month-grid">
          {monthCards.map((c, index) => (
            <div
              key={c.label}
              className="admin-month-card"
              style={{ animationDelay: `${index * 80 + 200}ms` }}
            >
              <p className="admin-month-period">{c.period}</p>
              <p className="admin-month-label">{c.label}</p>
              <p className="admin-month-value">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="admin-trend-card">
          <div className="admin-trend-card-header">
            <div>
              <h2 className="admin-section-title">Activity trend</h2>
              <p className="admin-section-sub">Last 30 days</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(['bookings', 'revenue', 'users'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTsMetric(m)}
                  className={`admin-metric-pill ${tsMetric === m ? 'is-active' : ''}`}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {latestPoint ? (
            <p className="admin-trend-latest">
              Latest: <strong>{latestPoint.value.toLocaleString()}</strong>
              <span className="text-gray-400"> · {latestPoint.date}</span>
            </p>
          ) : null}

          <MiniChart points={tsPoints ?? []} color={tsColors[tsMetric]} metric={tsMetric} />

          {tsPoints && tsPoints.length > 0 && (
            <div className="admin-trend-dates">
              <span>{tsPoints[0]?.date}</span>
              <span>{tsPoints[tsPoints.length - 1]?.date}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
